import * as React from 'react';
import styled from 'styled-components';
import { useBreakpoint } from 'hooks';
import {
  ListingDocumentType,
  ListingDocumentJsonType,
} from '@bb/domain/dist/types';
import { formatDocumentType } from 'utils';
import {
  Button,
  Icon,
  Dragger,
  FileList,
  UploadChangeParamType,
  UploadFileType,
  FileViewer,
  Modal,
  message,
  Text,
  theme,
} from 'components';
import SelectType from './SelectType';

interface UploadDocumentsProps {
  documents?: any[];
  setDocuments?: React.Dispatch<React.SetStateAction<any[]>>;
  description?: string;
  helperText?: React.ReactNode;
  multiple?: boolean;
  documentType?: ListingDocumentType;
}

type FileToViewType = {
  url: string;
  fileType: ListingDocumentJsonType['fileType'];
  name?: string;
};

const UploadDocuments: React.FC<UploadDocumentsProps> = ({
  documents,
  setDocuments,
  description,
  helperText,
  multiple = true,
  documentType,
}) => {
  const [fileToView, setFileToView] = React.useState<FileToViewType>(null);

  const onDocumentUpload = ({
    file,
  }: UploadChangeParamType<UploadFileType<any>>) => {
    const newFile = { ...file };
    if (
      newFile.response &&
      (newFile.response.status === 'error' || newFile?.error?.status >= 400)
    ) {
      const errorDetails =
        newFile.response?.message || newFile.response || 'Upload Error';
      message.error(`Error uploading file: ${newFile.name}. ${errorDetails}`);
      newFile.status = 'error';
    } else if (newFile.status !== 'done' || !newFile.response?.url) {
      newFile.status = 'uploading';
    } else if (newFile.status === 'done' && newFile.response?.url) {
      newFile.status = 'done';
    }

    const formattedFile = {
      uid: file.uid,
      name: file.name,
      mimetype: file.type,
      url: file.response?.url,
      size: file.size,
      status: newFile.status,
      updatedAt: file.lastModifiedDate,
      percent: file.percent,
    };

    setDocuments(prev => {
      if (!multiple) {
        return [formattedFile];
      }
      const addedFileIndex = prev.findIndex(({ uid }) => uid === newFile.uid);
      if (addedFileIndex > -1) {
        const newFiles = [...prev];
        newFiles[addedFileIndex] = {
          documentType: newFiles[addedFileIndex].documentType,
          ...formattedFile,
        };
        return newFiles;
      }
      return [...prev, formattedFile];
    });
  };
  const removeDocument = ({ uid }: { uid: string }) => {
    setDocuments(prev => prev.filter(document => document.uid !== uid));
  };

  const ModalTitle = (
    <StyledTitle>
      <span>Preview</span>
      <StyledCenteredGrid>{fileToView?.name}</StyledCenteredGrid>
    </StyledTitle>
  );

  return (
    <div>
      <Dragger
        onChange={onDocumentUpload}
        showIcon
        multiple={multiple}
        action="/upload/single"
        showUploadList={false}
        accept=".pdf,.jpg,.png"
        title="Click or drag a file to this area to upload"
        description={description}
        data-cy="drag-and-drop-file"
      >
        <StyledButtonSpacing>
          <Button>
            <Icon type="antdUploadOutlined" />
            Upload Files
          </Button>
        </StyledButtonSpacing>
        <FileList
          files={documents}
          onRemoveFile={removeDocument}
          singleColumn
          extra={file =>
            documentType ? (
              <Text>{formatDocumentType(documentType)}</Text>
            ) : (
              <SelectType
                uid={file.uid}
                documents={documents}
                setDocuments={setDocuments}
              />
            )
          }
          onClick={file =>
            setFileToView({
              ...file,
              fileType: file.mimetype,
            } as FileToViewType)
          }
        />
        <StyledText type="secondary" textAlign="center">
          {helperText}
        </StyledText>
      </Dragger>
      <Modal
        title={ModalTitle}
        centered
        open={Boolean(fileToView)}
        onCancel={() => setFileToView(null)}
        footer={null}
        destroyOnClose
        width="70vw"
        bodyStyle={{
          maxHeight: '70vh',
          overflow: 'auto',
          background: theme.getColor('background'),
        }}
      >
        <FileViewer files={[fileToView]} hideSpin />
      </Modal>
    </div>
  );
};

export default UploadDocuments;

const StyledButtonSpacing = styled.div`
  margin: 25px;
`;

const StyledText = styled(Text)`
  padding: 10px;
  margin: auto;
`;

const StyledTitle = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
`;
const StyledCenteredGrid = styled.span`
  justify-self: center;
`;
